/**
 * Live input masks and a caret-safe edit engine (material-standard §2.5).
 * Display may use separators and Persian digits; {@link MaskEditResult.raw} is always normalized ASCII
 * digits (and a single `.` for fractional toman when applicable).
 */

import {
  groupDigits,
  normalizeDigits,
  PERSIAN_DECIMAL_SEPARATOR,
  toPersianDigits,
} from './digits.js';
import { normalizeMobileInput } from './masking.js';

export interface MaskSpec {
  /** Maximum number of digit characters kept in {@link MaskEditResult.raw} (fraction excluded for amount). */
  readonly maxDigits: number;
  /** Formats a partial or complete digit run for display. */
  readonly formatDigits: (digits: string) => string;
  /** Pulls the digit run from arbitrary typed or pasted text. */
  readonly extractDigits: (text: string) => string;
}

export interface MaskEditResult {
  readonly display: string;
  readonly raw: string;
  readonly selectionEnd: number;
  readonly selectionStart: number;
}

const ASCII_DIGIT = /[0-9]/;
const PERSIAN_OR_ASCII_DIGIT = /[0-9۰-۹٠-٩]/;

/** Counts digit characters before `caret` in a display string (Persian or ASCII). */
export function digitIndexBeforeCaret(display: string, caret: number): number {
  let count = 0;
  const end = Math.max(0, Math.min(caret, display.length));
  for (let index = 0; index < end; index += 1) {
    if (PERSIAN_OR_ASCII_DIGIT.test(display[index] ?? '')) {
      count += 1;
    }
  }
  return count;
}

/** Caret position immediately after `digitIndex` digits in `display`. */
export function caretAfterDigitIndex(display: string, digitIndex: number): number {
  if (digitIndex <= 0) {
    return 0;
  }
  let count = 0;
  for (let index = 0; index < display.length; index += 1) {
    if (PERSIAN_OR_ASCII_DIGIT.test(display[index] ?? '')) {
      count += 1;
      if (count === digitIndex) {
        return index + 1;
      }
    }
  }
  return display.length;
}

function compactAsciiDigits(text: string): string {
  return normalizeDigits(text).replace(/\D/g, '');
}

function formatCardDigits(digits: string): string {
  const parts: string[] = [];
  for (let index = 0; index < digits.length; index += 4) {
    parts.push(digits.slice(index, index + 4));
  }
  return parts.join(' ');
}

function formatMobileDigits(digits: string): string {
  if (digits.length <= 4) {
    return digits;
  }
  if (digits.length <= 7) {
    return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  }
  return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
}

function formatShebaDigits(digits: string): string {
  const pattern = [2, 4, 4, 4, 4, 4, 2];
  const parts: string[] = [];
  let offset = 0;
  for (const size of pattern) {
    if (offset >= digits.length) {
      break;
    }
    parts.push(digits.slice(offset, offset + size));
    offset += size;
  }
  return parts.join(' ');
}

function formatSolarDateDigits(digits: string): string {
  if (digits.length <= 4) {
    return digits;
  }
  if (digits.length <= 6) {
    return `${digits.slice(0, 4)}/${digits.slice(4)}`;
  }
  return `${digits.slice(0, 4)}/${digits.slice(4, 6)}/${digits.slice(6)}`;
}

function extractMobileDigits(text: string): string {
  const e164 = normalizeMobileInput(text);
  if (e164 !== null) {
    return `0${e164.slice(3)}`;
  }
  return compactAsciiDigits(text).slice(0, 11);
}

const GROUPING = /[,٬،\s‌‏‎]/g;

function extractAmountDigits(text: string): string {
  const normalized = normalizeDigits(text).replace(GROUPING, '').replace(/[٫]/g, '.');
  const match = /^(\d*)(?:\.(\d?))?$/.exec(normalized);
  if (match === null) {
    return compactAsciiDigits(text).slice(0, 15);
  }
  const whole = (match[1] ?? '').replace(/^0+(?=\d)/, '').slice(0, 15);
  const fraction = match[2] ?? '';
  return fraction === '' ? whole : `${whole}.${fraction}`;
}

function formatAmountDigits(digits: string): string {
  const [whole = '', fraction = ''] = digits.split('.');
  const grouped = groupDigits(whole);
  if (fraction === '') {
    return grouped;
  }
  return `${grouped}${PERSIAN_DECIMAL_SEPARATOR}${toPersianDigits(fraction)}`;
}

/** `9900 0059 4923 9264` — sixteen ASCII digits in groups of four. */
export const CARD_NUMBER_MASK: MaskSpec = {
  extractDigits: (text) => compactAsciiDigits(text).slice(0, 16),
  formatDigits: formatCardDigits,
  maxDigits: 16,
};

/** `0912 345 6789` — eleven ASCII digits, local mobile shape. */
export const MOBILE_MASK: MaskSpec = {
  extractDigits: extractMobileDigits,
  formatDigits: formatMobileDigits,
  maxDigits: 11,
};

/** Ten ASCII digits, no separators. */
export const NATIONAL_CODE_MASK: MaskSpec = {
  extractDigits: (text) => compactAsciiDigits(text).slice(0, 10),
  formatDigits: (digits) => digits,
  maxDigits: 10,
};

/** Twenty-four ASCII digits grouped `82 0540 1026 8002 0817 9090 02` (prefix `IR` is separate). */
export const SHEBA_DIGITS_MASK: MaskSpec = {
  extractDigits: (text) => {
    const upper = normalizeDigits(text)
      .replace(/[\s-]+/g, '')
      .toUpperCase();
    const withoutCountry = upper.startsWith('IR') ? upper.slice(2) : upper;
    return withoutCountry.replace(/\D/g, '').slice(0, 24);
  },
  formatDigits: formatShebaDigits,
  maxDigits: 24,
};

/** Persian-grouped toman with at most one decimal digit (display only; rials conversion stays in money.ts). */
export const TOMAN_AMOUNT_MASK: MaskSpec = {
  extractDigits: extractAmountDigits,
  formatDigits: formatAmountDigits,
  maxDigits: 15,
};

/** `۱۴۰۵/۰۷/۰۵` with slashes inserted after the year and month; the raw value is ASCII digits. */
export const SOLAR_DATE_MASK: MaskSpec = {
  extractDigits: (text) => compactAsciiDigits(text).slice(0, 8),
  // Shown in Persian digits like the date under it (the value stays ASCII digits).
  formatDigits: (digits) => toPersianDigits(formatSolarDateDigits(digits)),
  maxDigits: 8,
};

/** Six (or fewer) ASCII digits for OTP / PIN / purchase code fields. */
export function codeMask(length: number): MaskSpec {
  return {
    extractDigits: (text) => compactAsciiDigits(text).slice(0, length),
    formatDigits: (digits) => digits,
    maxDigits: length,
  };
}

/**
 * Reconciles a controlled input value with a mask: strips and caps digits, reformats, and restores the
 * caret by digit index (material-standard §2.5).
 */
export function applyMaskedInput(
  spec: MaskSpec,
  inputValue: string,
  selectionStart: number,
  selectionEnd: number = selectionStart,
): MaskEditResult {
  const digitIndex = digitIndexBeforeCaret(inputValue, selectionStart);
  const raw = spec.extractDigits(inputValue).slice(0, spec.maxDigits);
  const display = spec.formatDigits(raw);
  const caret = caretAfterDigitIndex(display, digitIndex);
  return { display, raw, selectionEnd: caret, selectionStart: caret };
}

/**
 * Paste sanitizer for fixed-length codes (material-standard §2.4): normalize digits, prefer the first
 * exact-length run, otherwise strip and truncate.
 */
export function sanitizePastedDigits(text: string, length: number): string {
  const normalized = normalizeDigits(text);
  const exact = new RegExp(`\\d{${length}}`).exec(normalized);
  if (exact !== null) {
    return exact[0] ?? '';
  }
  return normalized.replace(/\D/g, '').slice(0, length);
}

/** Whether `character` is an ASCII digit (used in tests and caret helpers). */
export function isAsciiDigit(character: string): boolean {
  return ASCII_DIGIT.test(character);
}
